import React, {
  useLayoutEffect,
  useRef,
} from 'react';
import './live-news-globe-route.css';

const VISUAL_VIEWPORT_PROPERTIES = [
  '--lng-visual-viewport-left',
  '--lng-visual-viewport-top',
  '--lng-visual-viewport-width',
  '--lng-visual-viewport-height',
];
const viewportLeases = new WeakMap();

function withViewportFitCover(content) {
  return /(?:^|,)\s*viewport-fit\s*=\s*cover\s*(?:,|$)/i.test(content)
    ? content
    : `${content}${content ? ', ' : ''}viewport-fit=cover`;
}

export function acquireViewportFitCover(doc = document) {
  const nodes = doc.head.querySelectorAll('meta[name="viewport"]');
  if (nodes.length !== 1) {
    throw new Error('Expected one viewport meta.');
  }
  const node = nodes[0];
  let lease = viewportLeases.get(node);
  if (!lease) {
    const base = node.content;
    const owned = withViewportFitCover(base);
    node.content = owned;
    lease = { n: 0, b: base, v: owned };
    viewportLeases.set(node, lease);
  }
  lease.n += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const currentLease = viewportLeases.get(node);
    if (!currentLease || --currentLease.n > 0) return;
    viewportLeases.delete(node);
    if (node.content === currentLease.v) {
      node.content = currentLease.b;
    } else if (process.env.NODE_ENV !== 'production') {
      console.warn('Live News Globe preserved an externally changed viewport meta.');
    }
  };
}

function useRouteViewport(rootRef) {
  useLayoutEffect(() => acquireViewportFitCover(), []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const viewport = window.visualViewport;
    if (!root || !viewport) return undefined;
    let owned = [];
    let frame = null;

    const write = () => {
      frame = null;
      const values = [
        `${viewport.offsetLeft}px`,
        `${viewport.offsetTop}px`,
        `${viewport.width}px`,
        `${viewport.height}px`,
      ];
      VISUAL_VIEWPORT_PROPERTIES.forEach(
        (property, index) => root.style.setProperty(property, values[index]),
      );
      owned = values;
      root.setAttribute('data-lng-visual-viewport', '');
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(write);
    };
    window.addEventListener('resize', schedule);
    viewport.addEventListener('resize', schedule);
    viewport.addEventListener('scroll', schedule);
    write();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);
      VISUAL_VIEWPORT_PROPERTIES.forEach((property, index) => {
        if (root.style.getPropertyValue(property) === owned[index]) {
          root.style.removeProperty(property);
        }
      });
      if (root.getAttribute('data-lng-visual-viewport') === '') {
        root.removeAttribute('data-lng-visual-viewport');
      }
    };
  }, [rootRef]);
}

export default function LiveNewsGlobeRouteShell({ children }) {
  const rootRef = useRef(null);
  useRouteViewport(rootRef);

  return (
    <div
      ref={rootRef}
      className="live-news-route-shell"
      data-tra-live-news-route-shell=""
    >
      {children}
    </div>
  );
}
